import { useState } from 'react';
import { useLocation } from 'wouter';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FormBuilder } from '@/components/business/FormBuilder';
import { useAuth } from '@/hooks/useAuth';

/**
 * 第一版单租户登录页面标识。后端 Organization.slug 与此值一致。
 * 字段保持可见可编辑，以兼容未来多租户场景。
 */
const DEFAULT_TENANT_SLUG = '金专健康管理';

/**
 * 登录页
 *
 * 真实环境下通过 @jinzhuan/api-client 调用后端 `/api/v1/auth/login` 端点。
 * 开发环境下，若未显式注入真实 adapter，会自动回落到 `__dev__/mocks/devAuthAdapter`。
 * 生产构建中，dev mock 会被 tree-shake 移除，必须有真实 adapter 否则会抛错。
 *
 * 第一版单租户部署：机构标识字段预填 DEFAULT_TENANT_SLUG，保持可见可编辑。
 */
export default function LoginPage() {
  const { signIn } = useAuth();
  const [, setLocation] = useLocation();
  const [values, setValues] = useState<Record<string, string>>({
    tenantSlug: DEFAULT_TENANT_SLUG,
    email: '',
    password: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (next: Readonly<Record<string, string>>) => {
    setSubmitting(true);
    try {
      await signIn({
        tenantSlug: next.tenantSlug ?? '',
        email: next.email ?? '',
        password: next.password ?? '',
      });
      toast.success(import.meta.env.DEV ? '已建立开发态会话' : '登录成功');
      setLocation('/');
    } catch (error) {
      // 登录失败要显示明确错误，不暴露 token/PII
      const message = error instanceof Error ? error.message : '登录失败';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>金专系统 · 管理后台</CardTitle>
          <CardDescription>
            使用机构标识和工作邮箱登录。
            {import.meta.env.DEV ? (
              <span className="mt-2 block rounded border border-blue-400 bg-blue-50 px-2 py-1 text-xs text-blue-700">
                ℹ 开发模式：当前支持 DEV MOCK 或 真实后端。
              </span>
            ) : null}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormBuilder
            fields={[
              {
                name: 'tenantSlug',
                label: '机构标识',
                type: 'text',
                required: true,
                placeholder: '例如：金专健康管理',
                autoComplete: 'organization',
              },
              { name: 'email', label: '邮箱', type: 'email', required: true, autoComplete: 'username' },
              { name: 'password', label: '密码', type: 'password', required: true, autoComplete: 'current-password' },
            ]}
            values={values}
            onChange={(name, v) => setValues((prev) => ({ ...prev, [name]: v }))}
            onSubmit={handleSubmit}
            isSubmitting={submitting}
            submitLabel="登录"
          />
        </CardContent>
      </Card>
    </div>
  );
}
